/**
 * 解析 SHEIN「商品发布规范」query-publish-fill-in-standard。
 * cost_info.currency 必须以该接口 【currency】 返回的币种缩写为准，
 * 不能用 query-site-list 的站点展示币种冒充。
 *
 * SKU 件数 / 包装类型等依赖 fill_in_standard_list 的 show/required，
 * 且查询时必须带末级 category_id。
 */

function unwrapRoot(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.info && typeof raw.info === 'object') return raw.info;
  if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) return raw.data;
  return raw;
}

function unwrapInfo(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  if (raw.info != null) return raw.info;
  return raw;
}

/**
 * @returns {Map<string, { fieldKey: string, required: boolean, show: boolean, raw: object }>}
 */
export function parseFillInStandardMap(raw) {
  const info = unwrapInfo(raw);
  const list = info?.fill_in_standard_list
    || info?.fillInStandardList
    || info?.list
    || [];
  const map = new Map();
  (Array.isArray(list) ? list : []).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const key = String(row.field_key || row.fieldKey || '').trim();
    if (!key) return;
    map.set(key, {
      fieldKey: key,
      required: row.required === true || row.required === 'true',
      show: row.show !== false && row.show !== 'false',
      raw: row,
    });
  });
  return map;
}

/** 缺省 key 视为不可填（安全默认，避免误传触发 spmp0003） */
export function getFieldRule(map, fieldKey) {
  const hit = map instanceof Map ? map.get(String(fieldKey || '').trim()) : null;
  if (!hit) {
    return { show: false, required: false, fieldKey: String(fieldKey || '') };
  }
  return {
    fieldKey: hit.fieldKey,
    show: Boolean(hit.show),
    required: Boolean(hit.required),
  };
}

/**
 * 发布规范里「按类目动态必填」的字段中文名。
 * 服装类目通常只要几条基础字段，非服装类目（家居/3C/美妆/母婴…）常额外要求
 * 上架要求、最小备货数量、建议零售价、库存证明等；缺一条平台就整单驳回，
 * 且报错文案只有字段名，用户很难对上表单，所以这里统一给可读名称。
 */
export const PUBLISH_FIELD_LABELS = Object.freeze({
  shelf_require: '上架要求（到货 SHEIN 仓才上架）',
  shelf_way: '上架方式',
  minimum_stock_quantity: '最小备货数量',
  suggest_price: '建议零售价',
  supplier_barcode: '商品条码',
  reference_product_link: '商品信息参考链接',
  proof_of_stock: '库存证明',
  skc_title: 'SKC 标题',
  product_detail_pic: '站点详情图',
  original_image: '原图',
  ip_character: 'IP 授权',
  product_video: '商品视频',
  sample_spec: '样品信息',
  brand_code: '品牌',
  quantity_info: 'SKU 包装件数',
  package_type: 'SKU 包装类型',
  mall_state: 'SKU 销售状态',
});

/** 字段中文名（缺省回落 field_key，避免报错里出现空名） */
export function publishFieldLabel(fieldKey) {
  const key = String(fieldKey || '').trim();
  return PUBLISH_FIELD_LABELS[key] || key;
}

/**
 * 发布规范要求必填（required && show）的字段清单。
 * @returns {Array<{ fieldKey: string, label: string, rule: object }>}
 */
export function listRequiredStandardFields(standardRaw) {
  const map = parseFillInStandardMap(standardRaw);
  const out = [];
  map.forEach((row, fieldKey) => {
    if (!row?.show || !row.required) return;
    out.push({ fieldKey, label: publishFieldLabel(fieldKey), rule: getFieldRule(map, fieldKey) });
  });
  return out;
}

/**
 * SKU 包装相关能力（件数 / 包装类型）。
 * @returns {{
 *   quantityInfo: { show: boolean, required: boolean, fieldKey: string },
 *   packageType: { show: boolean, required: boolean, fieldKey: string },
 *   standardMap: Map<string, object>,
 * }}
 */
export function resolveSkuPackageCapabilities(standardRaw) {
  const standardMap = parseFillInStandardMap(standardRaw);
  return {
    quantityInfo: getFieldRule(standardMap, 'quantity_info'),
    packageType: getFieldRule(standardMap, 'package_type'),
    standardMap,
  };
}

/** 无类目规范时的安全默认：不展示、不传 SKU 件数/包装类型 */
export const EMPTY_SKU_PACKAGE_CAPS = Object.freeze({
  quantityInfo: Object.freeze({ show: false, required: false, fieldKey: 'quantity_info' }),
  packageType: Object.freeze({ show: false, required: false, fieldKey: 'package_type' }),
});

function collectCurrencyNodes(root) {
  if (!root || typeof root !== 'object') return [];
  const candidates = [
    root.currency,
    root.Currency,
    root.currency_list,
    root.currencyList,
    root.currencies,
    root?.data?.currency,
    root?.data?.currency_list,
    root?.data?.currencyList,
    root?.standard?.currency,
    root?.publish_standard?.currency,
  ];
  for (const node of candidates) {
    if (node == null) continue;
    if (Array.isArray(node) && node.length) return node;
    if (typeof node === 'string' && node.trim()) return [node];
    if (typeof node === 'object' && !Array.isArray(node)) {
      const keys = Object.keys(node);
      if (!keys.length) continue;
      if (node.code || node.currency || node.currency_code || node.currencyCode) {
        return [node];
      }
      return keys.map((key) => {
        const val = node[key];
        if (val && typeof val === 'object') {
          return { code: key, name: val.name || val.label || val.currency_name || key, ...val };
        }
        return { code: key, name: val == null ? key : String(val) };
      });
    }
  }

  // field_list / list 里 name=currency 的规范项
  const fieldLists = [
    root.field_list,
    root.fieldList,
    root.list,
    root?.data?.field_list,
    root?.data?.list,
  ];
  for (const list of fieldLists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const name = String(item.field || item.field_name || item.name || item.key || '').toLowerCase();
      if (name !== 'currency') continue;
      const opts = item.options || item.values || item.value_list || item.list || item.currency;
      if (Array.isArray(opts) && opts.length) return opts;
      if (typeof opts === 'string' && opts.trim()) return [opts];
    }
  }

  // 兜底：浅层扫描字段名含 currency 的数组
  for (const [key, val] of Object.entries(root)) {
    if (!/currency/i.test(key)) continue;
    if (Array.isArray(val) && val.length) return val;
  }
  return [];
}

function normalizeCurrencyEntry(entry) {
  if (entry == null) return null;
  if (typeof entry === 'string' || typeof entry === 'number') {
    const code = String(entry).trim();
    if (!code) return null;
    return { code, name: code };
  }
  if (typeof entry !== 'object') return null;
  const code = String(
    entry.code
    || entry.currency
    || entry.currency_code
    || entry.currencyCode
    || entry.abbr
    || entry.value
    || entry.key
    || '',
  ).trim();
  if (!code) return null;
  const name = String(
    entry.name
    || entry.label
    || entry.currency_name
    || entry.currencyName
    || entry.desc
    || code,
  ).trim();
  return { code, name };
}

/** @returns {{ code: string, name: string }[]} */
export function extractPublishCurrencies(raw) {
  const root = unwrapRoot(raw);
  const nodes = collectCurrencyNodes(root);
  const out = [];
  const seen = new Set();
  nodes.forEach((entry) => {
    const row = normalizeCurrencyEntry(entry);
    if (!row) return;
    const key = row.code.toUpperCase();
    // 保留平台原始缩写大小写；去重按大写
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}

/**
 * 店铺默认标题语种（来自 query-publish-fill-in-standard）。
 * 发品 multi_language_name_list 必须包含该语种，否则平台报「商品标题不能为空」(chinese_name 等)。
 */
export function extractDefaultLanguage(raw) {
  const root = unwrapRoot(raw);
  if (!root || typeof root !== 'object') return '';
  const direct = String(
    root.default_language
    || root.defaultLanguage
    || '',
  ).trim();
  if (direct) return direct;
  const list = Array.isArray(root.fill_in_standard_list)
    ? root.fill_in_standard_list
    : (Array.isArray(root.fillInStandardList) ? root.fillInStandardList : []);
  for (const row of list) {
    if (!row || typeof row !== 'object') continue;
    const key = String(row.field_key || row.fieldKey || '').trim();
    if (key === 'default_language' || key === 'defaultLanguage') {
      const v = String(
        row.default_language
        || row.defaultLanguage
        || row.field_value
        || row.fieldValue
        || row.value
        || '',
      ).trim();
      if (v) return v;
    }
  }
  return '';
}

/**
 * 在发布规范允许的币种中选默认值。
 * preferred 若在列表中则优先；否则取第一个。
 */
export function pickPublishCurrency(currencies = [], preferred = '') {
  const list = Array.isArray(currencies) ? currencies : [];
  if (!list.length) return '';
  const want = String(preferred || '').trim().toUpperCase();
  if (want) {
    const hit = list.find((c) => String(c.code || '').trim().toUpperCase() === want);
    if (hit) return String(hit.code || '').trim().toUpperCase() || want;
  }
  return String(list[0].code || '').trim().toUpperCase();
}
